import {
  Component,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { MAP_US_CENTER } from '@core/constants/map';
import { MapService } from '@core/services/map.service';
import { MapMarkerService } from '@core/services/map-marker.service';
import { IProperty } from '@shared/models/properties';
import { IOrganization } from '@shared/models/organization';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { setAuthModal } from '@state/auth/auth.actions';
import { selectAuth } from '@state/auth/auth.selectors';
import {
  selectPropertiesCount,
  selectPropertiesLoading,
} from '@state/properties/properties.selectors';
import { selectOrganizationUrlSlug } from '@state/organization/organization.selectors';

@UntilDestroy()
@Component({
  selector: 'app-home-mobile',
  templateUrl: './home-mobile.component.html',
  styleUrls: ['./home-mobile.component.scss'],
})
export class HomeMobileComponent implements OnInit, OnChanges {
  @Input({ required: true }) loading: boolean = false;
  @Input({ required: true }) properties: IProperty[] = [];

  protected isLoggedIn = false;
  protected isMobile = false;
  protected viewMode: 'map' | 'list' = 'list';
  protected markers: google.maps.marker.AdvancedMarkerElement[] = [];
  protected mapRef?: google.maps.Map;

  protected loading$ = this.store.select(selectPropertiesLoading);
  protected count$ = this.store.select(selectPropertiesCount);

  protected userOrg: IOrganization | undefined | null = undefined;
  protected marketplaceType: 'main' | 'external' | 'own' = 'main';

  constructor(
    private deviceService: DeviceDetectorService,
    private mapService: MapService,
    private mapMarkerService: MapMarkerService,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobile();

    this.store
      .select(selectAuth)
      .pipe(untilDestroyed(this))
      .subscribe((auth) => {
        this.isLoggedIn =
          !!auth.token &&
          !!auth.user?.emailConfirmedAt &&
          !!auth.user?.phoneNumberConfirmedAt;
        this.mapRef = undefined;
        this.autoCenterZoom();

        this.userOrg = auth.user?.organization;
        this.store
          .select(selectOrganizationUrlSlug)
          .pipe(untilDestroyed(this))
          .subscribe((orgUrlSlug) => {
            if (orgUrlSlug) {
              if (
                this.userOrg &&
                this.userOrg.shortName?.toLowerCase() ===
                  orgUrlSlug.toLowerCase()
              ) {
                this.marketplaceType = 'own';
              } else {
                this.marketplaceType = 'external';
              }
            } else {
              this.marketplaceType = 'main';
            }
          });
      });

    const authStorage = localStorage.getItem('home-view-mode');
    if (authStorage) {
      this.viewMode = authStorage as 'map' | 'list';
    }
  }

  toggleViewVode() {
    this.viewMode = this.viewMode === 'map' ? 'list' : 'map';
    localStorage.setItem('home-view-mode', this.viewMode);
  }

  /**
   * Check if it's necessary to recenter the map based on
   * - number of properties
   * - first and last property id
   * - first and last property latitude
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['properties']) {
      if (!this.mapRef) {
        setTimeout(() => {
          this.createMap();
          this.autoCenterZoom();
        }, 0);
      } else {
        const previousProps = changes['properties'].previousValue;
        const currentProps = changes['properties'].currentValue;

        const isFirstOrLastPropertyDifferent = () => {
          const firstPrevId = previousProps[0]?.id;
          const lastPrevId = previousProps[previousProps.length - 1]?.id;
          const firstCurrId = currentProps[0]?.id;
          const lastCurrId = currentProps[currentProps.length - 1]?.id;
          return firstPrevId !== firstCurrId || lastPrevId !== lastCurrId;
        };

        const isFirstOrLastPropertyLatitudeDifferent = () => {
          const firstPrevLat = previousProps[0]?.latitude;
          const lastPrevLat = previousProps[previousProps.length - 1]?.latitude;
          const firstCurrLat = currentProps[0]?.latitude;
          const lastCurrLat = currentProps[currentProps.length - 1]?.latitude;
          return firstPrevLat !== firstCurrLat || lastPrevLat !== lastCurrLat;
        };

        if (
          previousProps &&
          currentProps &&
          previousProps.length !== currentProps.length
        ) {
          this.autoCenterZoom();
        } else if (isFirstOrLastPropertyDifferent()) {
          this.autoCenterZoom();
        } else if (isFirstOrLastPropertyLatitudeDifferent()) {
          this.autoCenterZoom();
        }
      }
    }
  }

  createMap() {
    if (this.mapRef) return;
    this.mapRef = this.mapService.createMap('home-map', undefined);
  }

  /**
   * Center the map based on:
   * - authenticated status
   * - number of properties
   *
   * This method is also responsible
   * for setting the markers
   */
  autoCenterZoom() {
    if (!this.mapRef) return;
    const bounds = new google.maps.LatLngBounds();

    if (this.isLoggedIn && this.properties && this.properties.length > 0) {
      for (const property of this.properties) {
        if (
          property.latitude &&
          property.latitude !== '0' &&
          property.longitude &&
          property.longitude !== '0'
        ) {
          bounds.extend(
            new google.maps.LatLng(
              parseFloat(property.latitude),
              parseFloat(property.longitude)
            )
          );
        }
      }
      if (this.properties.length === 1) {
        this.mapRef.setCenter(bounds.getCenter());
        this.mapRef.setZoom(15);
      } else {
        this.mapRef.fitBounds(bounds, this.isMobile ? 45 : 90);
      }
      this.mapRef.setValues({ minZoom: this.isMobile ? 3 : 5 });
      setTimeout(() => {
        this.mapRef?.fitBounds(bounds, this.isMobile ? 45 : 90);
      }, 1500);
    } else {
      this.mapRef.setCenter(MAP_US_CENTER);
      this.mapRef.setValues({ zoom: 5 });
    }

    this.setMarkers();
  }

  // Always clear markers before setting them
  setMarkers() {
    this.markers.forEach((marker) => {
      this.mapService.removeAdvancedMarker(marker);
    });

    if (this.properties && this.mapRef) {
      this.markers = [];
      google.maps.event.clearListeners(this.mapRef, 'center_changed');
      google.maps.event.clearListeners(this.mapRef, 'click');
      this.properties.forEach((property: IProperty) => {
        const newMarker = this.mapService.createMarker(this.mapRef!, property);
        if (newMarker) this.markers.push(newMarker);
      });
    }
  }

  showAuthModal(authModal: 'signin' | 'signup') {
    this.store.dispatch(setAuthModal({ authModal }));
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler() {
    this.mapMarkerService.closeAllPreviews();
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.tagName === 'DIV' && !target.classList.length) {
      this.mapMarkerService.closeAllPreviews();
    }
  }
}
